import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Camera, Phone, MapPin, Trash2, ScanLine, Undo2, Lock } from "lucide-react";
import { ImprimirEtiquetas } from "@/components/domicilios/CargaEntrega";

export const Productos = ({ productos, big }) => (
  <div className="space-y-1">
    {productos.map((p, i) => (
      <div key={i} className={`${big ? "text-lg" : "text-base"} leading-tight`}>
        <b className="tabular-nums">{p.cantidad}×</b> <b>{p.producto}</b>
        <span className="text-zinc-600"> {[p.tamano, p.estilo, p.color].filter(Boolean).join(" · ")}</span>
      </div>
    ))}
  </div>
);

export const Guias = ({ guias }) => guias.length > 0 && (
  <div className="flex flex-wrap gap-1">{guias.map((g) => <span key={g.numero} className="bg-zinc-900 text-white font-mono text-sm px-2 py-1 rounded">{g.numero}</span>)}</div>
);

export const EmpaqueCard = ({ p, onOpen, onScan }) => (
  <div className={`bg-white border-2 ${p.prioridad ? "border-red-600" : "border-zinc-200"} rounded-lg p-4 shadow-sm`} data-testid={`emp-card-${p.numero}`}>
    <button onClick={onOpen} className="w-full text-left space-y-2" data-testid={`emp-open-${p.numero}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-2xl font-black tracking-tight">{p.numero}</span>
        <div className="flex gap-1">
          {p.anterior && <span className="bg-zinc-200 text-zinc-800 text-xs font-black px-2 py-1 rounded uppercase" title="Pedido de antes del último cierre mensual: en Hoja de Pedidos está en el Historial" data-testid={`emp-anterior-${p.numero}`}>Período anterior</span>}
          {p.prioridad && <span className="bg-red-600 text-white text-xs font-black px-2 py-1 rounded uppercase">Prioridad</span>}
          {p.cerrado && <span className="bg-zinc-800 text-white text-xs font-black px-2 py-1 rounded uppercase" data-testid={`emp-badge-cerrado-${p.numero}`}>Cerrado</span>}
          <span className={`text-xs font-black px-2 py-1 rounded uppercase ${p.empacado ? "bg-emerald-600 text-white" : "bg-amber-400 text-black"}`}>{p.empacado ? "Empacado" : "Pendiente"}</span>
        </div>
      </div>
      <div className="text-xl font-bold">{p.cliente}</div>
      {p.destino && <div className="text-sm text-zinc-500 flex gap-1"><MapPin className="w-4 h-4 shrink-0" />{p.destino}</div>}
      <Productos productos={p.productos} />
      {p.notas && <div className="text-sm bg-amber-50 border border-amber-200 rounded p-2 whitespace-pre-wrap" data-testid={`emp-notas-${p.numero}`}>{p.notas}</div>}
      <Guias guias={p.guias} />
      {p.empacado && <div className="text-sm text-zinc-600">Empacó <b>{p.empacado_por || "—"}</b> · {p.empacado_at}</div>}
      {p.fotos.length > 0 && <div className="flex gap-1 overflow-x-auto">{p.fotos.slice(0, 4).map((f) => <img key={f} src={f} alt="empaque" className="w-16 h-16 object-cover rounded border" />)}</div>}
    </button>
    {!p.empacado && (
      <button onClick={onScan} className="mt-3 w-full h-20 rounded-xl bg-black text-white text-2xl font-black uppercase flex items-center justify-center gap-2" data-testid={`emp-scan-${p.numero}`}>
        <ScanLine className="w-6 h-6" /> Escanear guía
      </button>
    )}
  </div>
);

const SubirFotos = ({ p, onDone }) => {
  const [busy, setBusy] = useState(0);
  const onFiles = async (e) => {
    const files = [...(e.target.files || [])];
    e.target.value = "";
    setBusy(files.length);
    for (const f of files) {
      const b64 = await new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(f); });
      try { await api.post(`/app-empaque/${p.id}/fotos`, { foto_base64: b64 }); } catch (err) { toast.error(formatApiError(err)); }
      setBusy((n) => n - 1);
    }
    toast.success("Fotos guardadas");
    onDone();
  };
  return (
    <label className="w-full h-20 rounded-xl border-2 border-black text-xl font-black uppercase flex items-center justify-center gap-2 cursor-pointer" data-testid="emp-fotos-add">
      <Camera className="w-6 h-6" /> {busy ? `Subiendo ${busy}…` : "Tomar / subir fotos"}
      <input type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={onFiles} disabled={!!busy} />
    </label>
  );
};

const Confirmar = ({ p, onDone }) => {
  const [resp, setResp] = useState(() => localStorage.getItem("emp_responsable") || "");
  const [paq, setPaq] = useState(p.guias.length || 1);
  const [lista, setLista] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/app-empaque/responsables").then(({ data }) => setLista(data)).catch(() => {}); }, []);
  useEffect(() => { setPaq(p.guias.length || 1); }, [p.guias.length]);
  const ok = p.guias.length > 0 && resp.trim().length >= 2 && Number(paq) === p.guias.length;
  const go = async () => {
    setBusy(true);
    try { await api.post(`/app-empaque/${p.id}/empacar`, { responsable: resp.trim(), paquetes: Number(paq) }); localStorage.setItem("emp_responsable", resp.trim()); toast.success(`${p.numero} EMPACADO`); onDone(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-lg border-2 border-emerald-600 p-4 space-y-3" data-testid="emp-confirmar">
      <div className="text-xs uppercase font-black text-emerald-700">Confirmar empaque</div>
      {p.guias.length === 0 && <div className="text-base font-bold text-red-600">1. Escanee primero la guía (o todas las guías).</div>}
      <label className="block text-sm font-bold">Responsable del empaque
        <input list="emp-resp-list" value={resp} onChange={(e) => setResp(e.target.value)} placeholder="Nombre" className="mt-1 w-full h-14 rounded-lg border-2 border-zinc-300 px-4 text-xl" data-testid="emp-responsable" />
        <datalist id="emp-resp-list">{lista.map((n) => <option key={n} value={n} />)}</datalist>
      </label>
      <label className="block text-sm font-bold">¿Cuántos paquetes tiene el pedido?
        <input type="number" min="1" value={paq} onChange={(e) => setPaq(e.target.value)} className="mt-1 w-full h-14 rounded-lg border-2 border-zinc-300 px-4 text-2xl font-black" data-testid="emp-paquetes" />
      </label>
      {p.guias.length > 0 && Number(paq) !== p.guias.length && <div className="text-base font-bold text-red-600" data-testid="emp-faltan-guias">Faltan guías: hay {p.guias.length} de {paq}. Escanee las demás.</div>}
      <button onClick={go} disabled={!ok || busy} className="w-full h-24 rounded-xl bg-emerald-600 disabled:opacity-40 text-white text-2xl font-black uppercase" data-testid="emp-confirmar-btn">{busy ? "Guardando…" : "Empacado"}</button>
    </div>
  );
};

export const EmpaqueDetalle = ({ p, onScan, onReload }) => {
  const run = async (fn, msg) => { try { await fn(); toast.success(msg); onReload(); } catch (e) { toast.error(formatApiError(e)); } };
  return (
    <div className="space-y-4" data-testid="emp-detalle">
      <div className={`rounded-lg border-2 p-4 ${p.prioridad ? "border-red-600" : "border-zinc-200"}`}>
        <div className="text-3xl font-black">{p.numero}</div>
        <div className="text-2xl font-bold mt-1">{p.cliente}</div>
        <div className="mt-2 space-y-1 text-base">
          {p.telefono && <div className="flex gap-2 items-center"><Phone className="w-5 h-5 text-emerald-700" /><b>{p.telefono}</b>{p.telefono2 && <b> / {p.telefono2}</b>}</div>}
          {(p.direccion || p.destino) && <div className="flex gap-2"><MapPin className="w-5 h-5 shrink-0 text-zinc-500" />{[p.direccion, p.destino].filter(Boolean).join(" · ")}</div>}
          {p.fecha_entrega && <div>Entrega: <b>{p.fecha_entrega}</b></div>}
        </div>
      </div>
      <div className="rounded-lg border-2 border-zinc-200 p-4"><div className="text-xs uppercase font-black text-zinc-500 mb-2">Productos</div><Productos productos={p.productos} big /><ImprimirEtiquetas orderId={p.id} className="mt-3" /></div>
      {p.notas && <div className="rounded-lg border-2 border-amber-300 bg-amber-50 p-4 text-base whitespace-pre-wrap"><div className="text-xs uppercase font-black text-amber-800 mb-1">Notas</div>{p.notas}</div>}
      <div className="rounded-lg border-2 border-zinc-200 p-4 space-y-2" data-testid="emp-guias">
        <div className="text-xs uppercase font-black text-zinc-500">Guías ({p.guias.length})</div>
        {p.guias.map((g) => (
          <div key={g.numero} className="flex items-center justify-between gap-2">
            <span className="font-mono text-xl font-bold">{g.numero}</span>
            <span className="text-xs text-zinc-500 flex-1">{g.by}</span>
            {!p.cerrado && !p.empacado && <button onClick={() => window.confirm(`¿Quitar la guía ${g.numero}?`) && run(() => api.delete(`/app-empaque/${p.id}/guia/${encodeURIComponent(g.numero)}`), "Guía quitada")} className="p-2 text-red-600" data-testid={`emp-guia-del-${g.numero}`}><Trash2 className="w-5 h-5" /></button>}
          </div>
        ))}
        {p.empacado && <div className="text-base">Empacado por <b>{p.empacado_por || "—"}</b> · {p.empacado_at}</div>}
      </div>
      {p.cerrado ? (
        <div className="rounded-lg bg-zinc-100 p-4 text-base flex gap-2 items-center" data-testid="emp-cerrado"><Lock className="w-5 h-5" /> En un cierre de empaque. Solo el administrador puede reabrirlo.</div>
      ) : (
        <div className="space-y-2">
          {!p.empacado && <button onClick={onScan} className="w-full h-20 rounded-xl bg-black text-white text-2xl font-black uppercase flex items-center justify-center gap-2" data-testid="emp-detalle-scan">
            <ScanLine className="w-7 h-7" /> {p.guias.length ? "Escanear otra guía" : "Escanear guía"}
          </button>}
          {!p.empacado && <Confirmar p={p} onDone={onReload} />}
          <SubirFotos p={p} onDone={onReload} />
          {p.empacado && <button onClick={() => window.confirm("¿Regresar este pedido a Pendiente?") && run(() => api.post(`/app-empaque/${p.id}/pendiente`), "Regresado a Pendiente")} className="w-full h-12 rounded-lg border-2 border-zinc-300 text-base font-bold uppercase flex items-center justify-center gap-2" data-testid="emp-revertir"><Undo2 className="w-5 h-5" /> Regresar a pendiente</button>}
        </div>
      )}
      <div className="rounded-lg border-2 border-zinc-200 p-4" data-testid="emp-fotos">
        <div className="text-xs uppercase font-black text-zinc-500 mb-2">Fotos de empaque ({p.fotos_det.length})</div>
        {p.fotos_det.length === 0 && <div className="text-sm text-zinc-400">Sin fotos.</div>}
        <div className="grid grid-cols-3 gap-2">
          {p.fotos_det.map((f) => (
            <a key={f.url} href={f.url} target="_blank" rel="noreferrer" className="block">
              <img src={f.url} alt="empaque" className="w-full aspect-square object-cover rounded border" />
              <div className="text-[10px] text-zinc-500 mt-0.5">{f.by} · {new Date(f.at).toLocaleString("es-GT", { timeZone: "America/Guatemala", hour: "2-digit", minute: "2-digit", day: "2-digit", month: "2-digit" })}</div>
            </a>
          ))}
        </div>
      </div>
      {p.historial?.length > 0 && (
        <div className="rounded-lg border-2 border-zinc-200 p-4" data-testid="emp-historial">
          <div className="text-xs uppercase font-black text-zinc-500 mb-2">Historial</div>
          {p.historial.map((h, i) => <div key={i} className="text-sm">{h.fecha} · {h.by} · {h.accion}</div>)}
        </div>
      )}
    </div>
  );
};
